// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import { TextField } from "@material-ui/core";
import { Box, Multiselect, Option } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";

import { CMS_DELIVERY_SLOT_CREATE_MUTATION, CMS_DELIVERY_SLOT_UPDATE_MUTATION } from "../cmsMutations";
import { CMS_DELIVERY_SLOTS_QUERY, PRODUCT_TYPES_LIST_QUERY } from "../cmsQueries";
import { CMSDeliverySlot } from "../types";
import CmsToggle from "./CmsToggle";

interface SlotDialogProps {
  open: boolean;
  slot?: CMSDeliverySlot | null;
  onClose: () => void;
}

const WEEKDAYS = [
  { label: "None (No blackout day)", value: "none" },
  { label: "Monday", value: "0" },
  { label: "Tuesday", value: "1" },
  { label: "Wednesday", value: "2" },
  { label: "Thursday", value: "3" },
  { label: "Friday", value: "4" },
  { label: "Saturday", value: "5" },
  { label: "Sunday", value: "6" },
];

export const SlotDialog: React.FC<SlotDialogProps> = ({ open, slot, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!slot;

  const { data: ptData } = useQuery(PRODUCT_TYPES_LIST_QUERY);
  const availableProductTypes =
    ptData?.productTypes?.edges?.map((e: any) => e.node) || [];

  const ptChoices: Option[] = availableProductTypes.map((pt: any) => ({
    label: pt.name,
    value: pt.id,
  }));

  const [label, setLabel] = useState(slot?.label || "");
  const [slotKey, setSlotKey] = useState(slot?.slotKey || "");
  const [timeWindow, setTimeWindow] = useState(slot?.timeWindow || "09:00 AM–11:00 AM");
  const [dayOffset, setDayOffset] = useState(slot ? slot.dayOffset.toString() : "1");
  const [skipWeekday, setSkipWeekday] = useState<string>(
    slot?.skipWeekday !== null && slot?.skipWeekday !== undefined ? String(slot.skipWeekday) : "none"
  );
  const [useNextSat, setUseNextSat] = useState(slot ? slot.useNextSat : false);
  const [cutoffTime, setCutoffTime] = useState(slot?.cutoffTime || "18:00");
  const [isActive, setIsActive] = useState(slot ? slot.isActive : true);
  const [selectedPtIds, setSelectedPtIds] = useState<string[]>(
    slot?.productTypes?.map(p => p.id) || []
  );

  useEffect(() => {
    if (slot) {
      setLabel(slot.label || "");
      setSlotKey(slot.slotKey || "");
      setTimeWindow(slot.timeWindow || "09:00 AM–11:00 AM");
      setDayOffset(
        slot.dayOffset !== null && slot.dayOffset !== undefined ? slot.dayOffset.toString() : "1"
      );
      setSkipWeekday(
        slot.skipWeekday !== null && slot.skipWeekday !== undefined ? String(slot.skipWeekday) : "none"
      );
      setUseNextSat(slot.useNextSat ?? false);
      setCutoffTime(slot.cutoffTime ? slot.cutoffTime.slice(0, 5) : "18:00");
      setIsActive(slot.isActive ?? true);
      setSelectedPtIds(slot.productTypes?.map(p => p.id) || []);
    } else {
      setLabel("");
      setSlotKey("");
      setTimeWindow("09:00 AM–11:00 AM");
      setDayOffset("1");
      setSkipWeekday("none");
      setUseNextSat(false);
      setCutoffTime("18:00");
      setIsActive(true);
      setSelectedPtIds([]);
    }
  }, [slot, open]);

  const [createSlot, { loading: creating }] = useMutation(CMS_DELIVERY_SLOT_CREATE_MUTATION, {
    refetchQueries: [{ query: CMS_DELIVERY_SLOTS_QUERY }],
    onCompleted: data => {
      if (data.cmsDeliverySlotCreate?.errors?.length) {
        notify({ status: "error", text: data.cmsDeliverySlotCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "Delivery slot created successfully" });
        onClose();
      }
    },
  });

  const [updateSlot, { loading: updating }] = useMutation(CMS_DELIVERY_SLOT_UPDATE_MUTATION, {
    refetchQueries: [{ query: CMS_DELIVERY_SLOTS_QUERY }],
    onCompleted: data => {
      if (data.cmsDeliverySlotUpdate?.errors?.length) {
        notify({ status: "error", text: data.cmsDeliverySlotUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "Delivery slot updated successfully" });
        onClose();
      }
    },
  });

  const handleSubmit = () => {
    if (!label || !slotKey || !timeWindow) {
      notify({ status: "error", text: "Label, slot key, and time window are required" });
      return;
    }

    const input: any = {
      label,
      slotKey,
      timeWindow,
      dayOffset: parseInt(dayOffset, 10) || 1,
      skipWeekday: skipWeekday === "none" ? null : parseInt(skipWeekday, 10),
      useNextSat,
      cutoffTime: cutoffTime || null,
      isActive,
      productTypeIds: selectedPtIds,
    };

    if (isEdit) {
      updateSlot({ variables: { id: slot.id, input } });
    } else {
      createSlot({ variables: { input } });
    }
  };

  const selectedPtOptions: Option[] = selectedPtIds.map(id => {
    const found = ptChoices.find(choice => choice.value === id);
    if (found) return found;
    const fromSlot = slot?.productTypes?.find(p => p.id === id);
    return {
      label: fromSlot?.name || id,
      value: id,
    };
  });

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit Delivery Slot" : "Create Delivery Slot"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <TextField
          label="Display Label"
          fullWidth
          value={label}
          onChange={e => setLabel(e.target.value)}
          helperText="e.g. Fresh Vegetables | Fresh Fruits"
          required
        />
        <TextField
          label="Slot Key (Unique ID)"
          fullWidth
          value={slotKey}
          onChange={e => setSlotKey(e.target.value)}
          disabled={isEdit}
          helperText="e.g. slot_1, slot_2, dairy_slot"
          required
        />
        <TextField
          label="Time Window String"
          fullWidth
          value={timeWindow}
          onChange={e => setTimeWindow(e.target.value)}
          helperText="e.g. 09:00 AM–11:00 AM or 04:00 PM–07:00 PM"
          required
        />
        <TextField
          label="Day Offset"
          fullWidth
          type="number"
          value={dayOffset}
          onChange={e => setDayOffset(e.target.value)}
          helperText="Days to advance from order date (1 = Next-Day Delivery)"
        />

        {/* Cutoff Time Selection */}
        <TextField
          label="Daily Cutoff Time"
          type="time"
          fullWidth
          value={cutoffTime}
          onChange={e => setCutoffTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
          helperText="Select daily cutoff time when this slot closes (e.g. 18:00 / 06:00 PM)"
        />

        {/* Skip Weekday using Select component */}
        <Select
          label="Skip Weekday (Blackout day of week)"
          name="skipWeekday"
          value={skipWeekday}
          options={WEEKDAYS}
          onChange={({ target }) => setSkipWeekday(target.value)}
        />

        {/* Mapped Product Types using Multiselect */}
        <Box display="flex" flexDirection="column" gap={1}>
          <Multiselect
            size="medium"
            options={ptChoices}
            label="Mapped Product Types"
            placeholder="Search and select product types..."
            value={selectedPtOptions}
            onChange={values => {
              setSelectedPtIds(
                values.map((v: any) => (typeof v === "string" ? v : v.value))
              );
            }}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={2} paddingTop={2}>
          <CmsToggle
            label="Deliver on Upcoming Saturday (useNextSat)"
            description="Slot automatically targets the coming Saturday regardless of cutoff"
            checked={useNextSat}
            onChange={setUseNextSat}
          />

          <CmsToggle
            label="Active Slot"
            description="Enable this delivery slot for customers at checkout"
            checked={isActive}
            onChange={setIsActive}
          />
        </Box>
      </Box>
    </ActionDialog>
  );
};

export default SlotDialog;
